import { Handle, Position, type NodeProps } from '@xyflow/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Edit,
  ExternalLink,
  Gift,
  Globe,
  Package,
  Plus,
  Repeat,
  ShoppingBag,
  ShoppingCart,
  Tag,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OfferInfo, ProductFunnelNodeData } from '../utils/funnelTypes';

function ProductFunnelNodeComponent({ data, selected }: NodeProps) {
  const { t } = useTranslation('funnel');
  const nodeData = data as unknown as ProductFunnelNodeData;
  const [showOffers, setShowOffers] = useState(false);
  const [expandedOffers, setExpandedOffers] = useState<Set<string>>(new Set());

  const hasImage = !!nodeData.imageUrl;

  const statusColors: Record<string, string> = {
    active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    inactive: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
    blocked: 'bg-red-500/20 text-red-400 border-red-500/30',
    revision: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    analysis: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };
  const statusLabels: Record<string, string> = {
    active: t('status_active'),
    inactive: t('status_inactive'),
    blocked: t('status_blocked'),
    revision: t('status_revision'),
    analysis: t('status_analysis'),
  };

  const intervalLabels: Record<string, string> = {
    daily: t('interval_daily'),
    weekly: t('interval_weekly'),
    monthly: t('interval_monthly'),
    yearly: t('interval_yearly'),
  };

  const toggleOffer = (offerId: string) => {
    setExpandedOffers((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) next.delete(offerId);
      else next.add(offerId);
      return next;
    });
  };

  const truncateLink = (link: string) => {
    try {
      const url = new URL(link);
      return url.hostname + (url.pathname.length > 12 ? url.pathname.slice(0, 12) + '…' : url.pathname);
    } catch {
      return link.length > 25 ? link.slice(0, 25) + '…' : link;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className={`
        relative rounded-xl border bg-[#18181B]/95 backdrop-blur-md shadow-xl
        min-w-[360px] max-w-[400px] overflow-visible transition-all duration-200 group
        ${selected ? 'border-primary shadow-primary/20' : 'border-[#27272a] hover:border-primary/40'}
      `}
    >
      <div className="h-0.5 w-full bg-gradient-to-r from-primary/60 via-primary to-primary/60 rounded-xl" />

      <div className="p-3.5">
        <div className="flex items-center gap-3">
          {hasImage ? (
            <img
              src={nodeData.imageUrl}
              alt={nodeData.label}
              className="h-10 w-10 rounded-lg object-cover ring-1 ring-primary/30"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 ring-1 ring-primary/30">
              <Package className="h-5 w-5 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-wider text-primary/70">{t('product')}</p>
            <p className="text-sm font-semibold text-white truncate">{nodeData.label}</p>
          </div>
          <div className="flex items-center gap-1">
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-medium ${
                statusColors[nodeData.status] || statusColors.inactive
              }`}
            >
              <span className="mr-1 h-1.5 w-1.5 rounded-full bg-current" />
              {statusLabels[nodeData.status] || nodeData.status}
            </span>
          </div>
        </div>

        {(nodeData.totalSales != null || nodeData.revenue) && (
          <div className="mt-3 flex items-center gap-3 rounded-md bg-primary/5 border border-primary/10 px-2.5 py-1.5">
            <BarChart3 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
            {nodeData.totalSales != null && (
              <span className="inline-flex items-center gap-1 text-[10px] text-zinc-300">
                <ShoppingBag className="h-3 w-3 text-primary/70" />
                {nodeData.totalSales.toLocaleString()} {t('sales')}
              </span>
            )}
            {nodeData.revenue && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary">
                <DollarSign className="h-3 w-3" />
                {nodeData.revenue}
              </span>
            )}
          </div>
        )}

        <button
          data-action="toggle-offers"
          onClick={(e) => {
            e.stopPropagation();
            setShowOffers(!showOffers);
          }}
          className="mt-3 w-full flex items-center justify-between rounded-lg bg-[#27272a]/50 border border-[#3f3f46]/50 px-3 py-2 text-[11px] font-medium text-zinc-300 hover:bg-[#27272a] transition-colors"
        >
          <div className="flex items-center gap-2">
            <Tag className="h-3.5 w-3.5 text-cyan-400" />
            <span>{nodeData.offers.length === 1 ? t('offers_count', { count: nodeData.offers.length }) : t('offers_count_plural', { count: nodeData.offers.length })}</span>
          </div>
          {showOffers ? (
            <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-zinc-500" />
          )}
        </button>

        <AnimatePresence>
          {showOffers && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-2 space-y-2 max-h-[180px] overflow-y-auto pr-0.5">
                {nodeData.offers.map((offer) => (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    isExpanded={expandedOffers.has(offer.id)}
                    onToggle={() => toggleOffer(offer.id)}
                    truncateLink={truncateLink}
                    intervalLabels={intervalLabels}
                  />
                ))}

                {nodeData.offers.length === 0 && (
                  <div className="text-center py-4">
                    <Tag className="h-6 w-6 text-zinc-600 mx-auto mb-2" />
                    <p className="text-[10px] text-zinc-500">{t('no_offers')}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <button
            data-action="add-offer"
            className="flex items-center justify-center gap-1 rounded-md bg-cyan-500/10 border border-cyan-500/20 px-2 py-1.5 text-[9px] font-medium text-cyan-400 hover:bg-cyan-500/20 transition-colors"
          >
            <Plus className="h-3 w-3 pointer-events-none" />
            <Tag className="h-3 w-3 pointer-events-none" />
            {t('add_offer')}
          </button>
          <button
            data-action="add-checkout"
            className="flex items-center justify-center gap-1 rounded-md bg-purple-500/10 border border-purple-500/20 px-2 py-1.5 text-[9px] font-medium text-purple-400 hover:bg-purple-500/20 transition-colors"
          >
            <Plus className="h-3 w-3 pointer-events-none" />
            <ShoppingCart className="h-3 w-3 pointer-events-none" />
            {t('add_checkout')}
          </button>
          <button
            data-action="add-order-bump"
            className="flex items-center justify-center gap-1 rounded-md bg-amber-500/10 border border-amber-500/20 px-2 py-1.5 text-[9px] font-medium text-amber-400 hover:bg-amber-500/20 transition-colors"
          >
            <Plus className="h-3 w-3 pointer-events-none" />
            <Gift className="h-3 w-3 pointer-events-none" />
            {t('add_order_bump')}
          </button>
          <button
            data-action="add-upsell"
            className="flex items-center justify-center gap-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 px-2 py-1.5 text-[9px] font-medium text-emerald-400 hover:bg-emerald-500/20 transition-colors"
          >
            <Plus className="h-3 w-3 pointer-events-none" />
            <TrendingUp className="h-3 w-3 pointer-events-none" />
            {t('add_upsell')}
          </button>
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        id="purchase"
        className="!w-3 !h-3 !bg-primary !border-2 !border-[#18181B] !-bottom-1.5"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="bump"
        className="!w-2 !h-2 !bg-amber-500 !border-2 !border-[#18181B] !-right-1"
        style={{ top: '50%' }}
      />
    </motion.div>
  );
}

function OfferCard({
  offer,
  isExpanded,
  onToggle,
  truncateLink,
  intervalLabels,
}: {
  offer: OfferInfo;
  isExpanded: boolean;
  onToggle: () => void;
  truncateLink: (link: string) => string;
  intervalLabels: Record<string, string>;
}) {
  const { t } = useTranslation('funnel');
  const isRecurring = offer.paymentType === 'recurring';

  return (
    <div className="rounded-lg border border-[#27272a] bg-[#09090b]/80 overflow-hidden">
      <div className="p-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-cyan-500/15 ring-1 ring-cyan-500/30 flex-shrink-0">
              <Tag className="h-3.5 w-3.5 text-cyan-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-medium text-white truncate">{offer.name}</p>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold text-cyan-400">
                  {offer.priceFormatted}
                  {isRecurring && offer.recurringInterval && (
                    <span className="text-[8px] font-normal text-zinc-500">
                      {intervalLabels[offer.recurringInterval] || ''}
                    </span>
                  )}
                </span>
                {isRecurring && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-cyan-500/10 px-1.5 py-0.5 text-[7px] font-medium text-cyan-400">
                    <Repeat className="h-2 w-2" /> {t('rec')}
                  </span>
                )}
                <span className="rounded-full bg-zinc-800 px-1.5 py-0.5 text-[7px] font-medium text-zinc-500">
                  {offer.currency}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {offer.conversions != null && (
              <span className="text-[8px] text-zinc-400">{offer.conversions} {t('sales')}</span>
            )}
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button
                data-action="edit"
                data-node-type="offer"
                data-offer-id={offer.id}
                className="flex h-5 w-5 items-center justify-center rounded-md hover:bg-cyan-500/20 transition-colors"
                title={t('edit_offer')}
              >
                <Edit className="h-2.5 w-2.5 text-cyan-400 pointer-events-none" />
              </button>
              <button
                data-action="delete"
                data-node-type="offer"
                data-offer-id={offer.id}
                className="flex h-5 w-5 items-center justify-center rounded-md hover:bg-red-500/20 transition-colors"
                title={t('delete_offer')}
              >
                <Trash2 className="h-2.5 w-2.5 text-red-400 pointer-events-none" />
              </button>
            </div>
          </div>
        </div>

        {offer.checkouts.length > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="mt-2 w-full flex items-center justify-between rounded-md bg-[#1a1a1d] border border-[#27272a] px-2 py-1 text-[9px] text-zinc-400 hover:bg-[#222] transition-colors"
          >
            <div className="flex items-center gap-1.5">
              <ShoppingCart className="h-3 w-3 text-purple-400" />
              <span>{offer.checkouts.length === 1 ? t('checkouts_count', { count: offer.checkouts.length }) : t('checkouts_count_plural', { count: offer.checkouts.length })}</span>
            </div>
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        )}

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <div className="mt-1.5 space-y-1.5">
                {offer.checkouts.map((checkout) => (
                  <div
                    key={checkout.id}
                    className="rounded-md border border-[#27272a] bg-[#111113] overflow-hidden"
                  >
                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-[#0d0d0f] border-b border-[#27272a]">
                      <div className="flex gap-0.5">
                        <span className="h-1 w-1 rounded-full bg-red-500/60" />
                        <span className="h-1 w-1 rounded-full bg-yellow-500/60" />
                        <span className="h-1 w-1 rounded-full bg-green-500/60" />
                      </div>
                      <div className="flex-1 flex items-center gap-1 rounded bg-[#1a1a1d] px-1 py-0.5 ml-0.5">
                        <Globe className="h-2 w-2 text-zinc-600 flex-shrink-0" />
                        <span className="text-[6px] text-zinc-500 truncate">
                          {truncateLink(checkout.link)}
                        </span>
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-1 py-0.5 text-[6px] font-medium ${
                          checkout.isActive
                            ? 'bg-emerald-500/15 text-emerald-400'
                            : 'bg-zinc-500/15 text-zinc-400'
                        }`}
                      >
                        <span className="mr-0.5 h-1 w-1 rounded-full bg-current" />
                        {checkout.isActive ? t('on') : t('off')}
                      </span>
                    </div>

                    <div className="px-2 py-1.5">
                      <span className="text-[9px] font-medium text-white truncate block">
                        {checkout.name}
                      </span>
                    </div>

                    <div className="flex border-t border-[#27272a]">
                      <button
                        data-action="edit-checkout"
                        data-checkout-id={checkout.id}
                        data-offer-id={offer.id}
                        className="flex-1 flex items-center justify-center gap-0.5 py-1 text-[8px] text-purple-400 hover:bg-purple-500/10 transition-colors"
                      >
                        <Edit className="h-2 w-2 pointer-events-none" />
                        {t('edit')}
                      </button>
                      <div className="w-px bg-[#27272a]" />
                      <button
                        data-action="open-checkout"
                        data-checkout-id={checkout.id}
                        className="flex-1 flex items-center justify-center gap-0.5 py-1 text-[8px] text-zinc-400 hover:bg-zinc-500/10 transition-colors"
                      >
                        <ExternalLink className="h-2 w-2 pointer-events-none" />
                        {t('open')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export const ProductFunnelNode = memo(ProductFunnelNodeComponent);
